'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import { Menu, X } from 'lucide-react';
import LiquidGlassButton from '@/components/LiquidGlassButton';

const navLinks = [
  { label: 'About', href: '/about' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Leaderboard', href: '/leaderboard' },
  { label: 'Affiliate', href: '/affiliate' },
  { label: 'Payments', href: '/payments' },
  { label: 'FAQ', href: '/faq' },
];

export default function HomepageNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Promo Banner */}
      {!promoDismissed && (
        <div className="relative z-50 bg-primary/90 text-white text-center py-2.5 px-4 text-sm font-medium">
          <span>🔥 Limited Time: Use code </span>
          <span className="font-mono-data font-bold bg-white/20 px-2 py-0.5 rounded mx-1">HiyaPrime20</span>
          <span> for 20% off your first challenge</span>
          <button
            onClick={() => setPromoDismissed(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/20 transition-colors"
            aria-label="Dismiss promo"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {/* Navbar */}
      <nav
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
          <Link href="/" className="flex items-center" suppressHydrationWarning>
            <img src="/assets/images/hiyaprimelogo.png" alt="HiyaPrime" style={{ width: 180, height: 60, objectFit: 'contain' }} suppressHydrationWarning />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <a
                key={`nav-${link?.label}`}
                href={link?.href}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
              >
                {link?.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link href="/login">
              <LiquidGlassButton variant="outline" className="px-4 py-2 text-sm">
                Sign In
              </LiquidGlassButton>
            </Link>
            <Link href="/register">
              <LiquidGlassButton variant="primary" className="px-4 py-2 text-sm">
                Get Funded
              </LiquidGlassButton>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted text-muted-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-card border-t border-border px-6 py-4 space-y-1 animate-fade-in">
            {navLinks?.map((link) => (
              <a
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </a>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <Link href="/login">
                <LiquidGlassButton variant="outline" className="px-4 py-2 text-sm text-center">
                  Sign In
                </LiquidGlassButton>
              </Link>
              <Link href="/register">
                <LiquidGlassButton variant="primary" className="px-4 py-2 text-sm text-center">
                  Get Funded
                </LiquidGlassButton>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}